import { useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import {
  ArrowLeft,
  ArrowUpDown,
  Building2,
  ChevronDown,
  ChevronUp,
  Download,
  Droplets,
  Factory,
  Package,
  PackageSearch,
  Search,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  getStockAt,
  useOilTypeStockByPlant,
  useOilTypes,
  useRawMaterialStockByPlant,
  useRawMaterials,
} from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import { useProductStockList, useReservedNotDispatched } from "~/hooks/wms";

import {
  type InsumoStockRow,
  PLANT_LABEL,
  type Plant,
  type ProductFisicoRow,
  exportAll,
  exportInsumosTab,
  exportMateriaPrimaTab,
  exportProductsTab,
} from "./components/consolidated-stock/exportToExcel";

type PlantFilter = "all" | Plant;
type TabValue = "products" | "insumos" | "materia_prima";

type SortDirection = "asc" | "desc";

type ProductSortKey =
  | "sku"
  | "product_name"
  | "fisico"
  | "no_despachado"
  | "disponible";

type InsumoSortKey =
  | "code"
  | "name"
  | "unit"
  | "plant"
  | "stock"
  | "minimumStock";

const PLANTS: Plant[] = ["P1", "P2"];

const OIL_UNIT = "ton";

const formatNumber = (value: number, decimals = 2) =>
  new Intl.NumberFormat("es-CL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);

const compareValues = (a: unknown, b: unknown): number => {
  const aNull = a === null || a === undefined || a === "";
  const bNull = b === null || b === undefined || b === "";
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  const aNum = typeof a === "string" ? Number(a) : NaN;
  const bNum = typeof b === "string" ? Number(b) : NaN;
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
  return String(a).localeCompare(String(b), "es", { sensitivity: "base" });
};

export function ConsolidatedStockPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<TabValue>("products");
  const [searchInput, setSearchInput] = useState("");
  const [plantFilter, setPlantFilter] = useState<PlantFilter>("all");

  const [productSort, setProductSort] = useState<{
    key: ProductSortKey;
    direction: SortDirection;
  }>({ key: "sku", direction: "asc" });
  const [insumoSort, setInsumoSort] = useState<{
    key: InsumoSortKey;
    direction: SortDirection;
  }>({ key: "code", direction: "asc" });
  const [materiaPrimaSort, setMateriaPrimaSort] = useState<{
    key: InsumoSortKey;
    direction: SortDirection;
  }>({ key: "code", direction: "asc" });

  const toggleProductSort = (key: ProductSortKey) =>
    setProductSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  const toggleInsumoSort = (key: InsumoSortKey) =>
    setInsumoSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  const toggleMateriaPrimaSort = (key: InsumoSortKey) =>
    setMateriaPrimaSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );

  // Data
  const { data: oilTypes = [], isLoading: oilsLoading } = useOilTypes();
  const { data: rawMaterials = [], isLoading: materialsLoading } =
    useRawMaterials();
  const { data: oilStockByPlant } = useOilTypeStockByPlant();
  const { data: rawMaterialStockByPlant } = useRawMaterialStockByPlant();
  const { data: productStockList = [], isLoading: productsLoading } =
    useProductStockList();
  const { data: reservedMap } = useReservedNotDispatched();

  const insumosLoading = materialsLoading;
  const materiaPrimaLoading = oilsLoading;

  const searchTerm = searchInput.trim().toLowerCase();

  const matchesSearch = (text: string | null | undefined) =>
    !searchTerm || (text ?? "").toLowerCase().includes(searchTerm);

  // ---------- Materia Prima (oils) ----------
  const materiaPrimaRows: InsumoStockRow[] = useMemo(() => {
    const rows: InsumoStockRow[] = [];
    for (const oil of oilTypes) {
      for (const plant of PLANTS) {
        const stock = getStockAt(oilStockByPlant, oil.id, plant);
        // Negative balances must stay visible (data errors to correct), only
        // zero-stock rows are hidden.
        if (stock !== 0) {
          rows.push({
            kind: "oil",
            id: oil.id,
            code: oil.code,
            name: oil.name,
            unit: OIL_UNIT,
            plant,
            stock,
            minimumStock: oil.minimum_stock_liters ?? null,
            oil,
          });
        }
      }
    }
    return rows;
  }, [oilTypes, oilStockByPlant]);

  // ---------- Insumos (raw materials) ----------
  const insumosRows: InsumoStockRow[] = useMemo(() => {
    const rows: InsumoStockRow[] = [];
    for (const material of rawMaterials) {
      if (material.exclude_from_inventory) continue;
      for (const plant of PLANTS) {
        const stock = getStockAt(rawMaterialStockByPlant, material.id, plant);
        if (stock !== 0) {
          rows.push({
            kind: "raw_material",
            id: material.id,
            code: material.code,
            name: material.name,
            unit: material.unit,
            plant,
            stock,
            minimumStock: material.minimum_stock_level ?? null,
            material,
          });
        }
      }
    }
    return rows;
  }, [rawMaterials, rawMaterialStockByPlant]);

  const filteredInsumosRows = useMemo(() => {
    const filtered = insumosRows.filter(
      (r) =>
        (plantFilter === "all" || r.plant === plantFilter) &&
        (matchesSearch(r.code) || matchesSearch(r.name))
    );
    const { key, direction } = insumoSort;
    const sign = direction === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => sign * compareValues(a[key], b[key]));
  }, [insumosRows, plantFilter, searchTerm, insumoSort]);

  const filteredMateriaPrimaRows = useMemo(() => {
    const filtered = materiaPrimaRows.filter(
      (r) =>
        (plantFilter === "all" || r.plant === plantFilter) &&
        (matchesSearch(r.code) || matchesSearch(r.name))
    );
    const { key, direction } = materiaPrimaSort;
    const sign = direction === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => sign * compareValues(a[key], b[key]));
  }, [materiaPrimaRows, plantFilter, searchTerm, materiaPrimaSort]);

  // ---------- Finished products (físico / no despachado / disponible) ----------
  // físico = products.stock (single source of truth); it has no bodega/plant
  // dimension (WMS positions are retired), so the plant filter does not apply here.
  const productRows: ProductFisicoRow[] = useMemo(
    () =>
      productStockList.map((p) => {
        const reserved = reservedMap?.get(p.product_id) ?? 0;
        return {
          product_id: p.product_id,
          sku: p.sku,
          product_name: p.name,
          unit: p.unit,
          fisico: p.stock,
          no_despachado: reserved,
          disponible: p.stock - reserved,
        };
      }),
    [productStockList, reservedMap]
  );

  const filteredProductRows: ProductFisicoRow[] = useMemo(() => {
    const filtered = productRows.filter(
      (r) => matchesSearch(r.product_name) || matchesSearch(r.sku)
    );
    const { key, direction } = productSort;
    const sign = direction === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => sign * compareValues(a[key], b[key]));
  }, [productRows, searchTerm, productSort]);

  // ---------- Export handlers ----------
  const handleExportCurrent = () => {
    try {
      if (activeTab === "insumos") exportInsumosTab(filteredInsumosRows);
      else if (activeTab === "materia_prima")
        exportMateriaPrimaTab(filteredMateriaPrimaRows);
      else exportProductsTab(filteredProductRows);
      toast({
        title: "Excel exportado",
        description: "El archivo se ha descargado correctamente.",
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "No se pudo exportar el archivo Excel.",
        variant: "destructive",
      });
    }
  };

  const handleExportAll = () => {
    try {
      exportAll(insumosRows, materiaPrimaRows, productRows);
      toast({
        title: "Excel consolidado exportado",
        description: "El archivo con las dos hojas se ha descargado.",
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "No se pudo exportar el archivo Excel.",
        variant: "destructive",
      });
    }
  };

  // ---------- Summary metrics for active tab ----------
  const summaryCards = useMemo(() => {
    if (activeTab === "products") {
      return [
        {
          label: "Productos en stock",
          value: new Set(
            filteredProductRows.map((r) => r.product_id)
          ).size.toString(),
          icon: Package,
        },
        {
          label: "Cajas físicas (total)",
          value: formatNumber(
            filteredProductRows.reduce((acc, r) => acc + r.fisico, 0),
            0
          ),
          icon: PackageSearch,
        },
        {
          label: "Disponible (total)",
          value: formatNumber(
            filteredProductRows.reduce((acc, r) => acc + r.disponible, 0),
            0
          ),
          icon: Building2,
        },
      ];
    }
    const rows =
      activeTab === "materia_prima"
        ? filteredMateriaPrimaRows
        : filteredInsumosRows;
    const itemIds = new Set(rows.map((r) => `${r.kind}:${r.id}`));
    return [
      {
        label:
          activeTab === "materia_prima"
            ? "Materias primas en stock"
            : "Insumos en stock",
        value: itemIds.size.toString(),
        icon: activeTab === "materia_prima" ? Droplets : Factory,
      },
      {
        label: "Plantas",
        value: new Set(rows.map((r) => r.plant)).size.toString(),
        icon: Building2,
      },
    ];
  }, [
    activeTab,
    filteredProductRows,
    filteredInsumosRows,
    filteredMateriaPrimaRows,
  ]);

  return (
    <PageContainer>
      <PageHeader
        title="Stock Consolidado"
        description="Insumos y productos terminados en un solo lugar"
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/wms")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="h-9">
                <Download className="mr-2 h-4 w-4" />
                Exportar Excel
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[260px]">
              <DropdownMenuLabel>Descargar</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleExportCurrent}>
                Exportar pestaña actual
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportAll}>
                Exportar todo (un archivo)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      <div
        className={`grid gap-4 ${
          summaryCards.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2"
        }`}
      >
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.label}>
              <CardContent className="flex items-center gap-3 pt-6">
                <div className="rounded-lg bg-slate-100 p-2">
                  <Icon className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{card.label}</p>
                  <p className="text-xl font-bold">{card.value}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[260px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, código o SKU..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="h-9 pl-9"
              />
            </div>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as TabValue)}
          >
            <TabsList>
              <TabsTrigger value="products" className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                Productos Terminados
              </TabsTrigger>
              <TabsTrigger value="insumos" className="flex items-center gap-2">
                <Factory className="h-4 w-4" />
                Insumos
              </TabsTrigger>
              <TabsTrigger
                value="materia_prima"
                className="flex items-center gap-2"
              >
                <Droplets className="h-4 w-4" />
                Materia Prima
              </TabsTrigger>
            </TabsList>

            {activeTab !== "products" && (
              <Tabs
                value={plantFilter}
                onValueChange={(v) => setPlantFilter(v as PlantFilter)}
                className="mt-4"
              >
                <TabsList>
                  <TabsTrigger value="all">Todas las plantas</TabsTrigger>
                  <TabsTrigger value="P1">Planta 1</TabsTrigger>
                  <TabsTrigger value="P2">Planta 2</TabsTrigger>
                </TabsList>
              </Tabs>
            )}

            <TabsContent value="products" className="mt-4">
              {productsLoading ? (
                <LoadingTable />
              ) : filteredProductRows.length === 0 ? (
                <EmptyState message="No hay productos terminados con los filtros aplicados" />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader
                        label="SKU"
                        active={productSort.key === "sku"}
                        direction={productSort.direction}
                        onClick={() => toggleProductSort("sku")}
                      />
                      <SortableHeader
                        label="Producto"
                        active={productSort.key === "product_name"}
                        direction={productSort.direction}
                        onClick={() => toggleProductSort("product_name")}
                      />
                      <SortableHeader
                        label="Stock físico"
                        align="right"
                        active={productSort.key === "fisico"}
                        direction={productSort.direction}
                        onClick={() => toggleProductSort("fisico")}
                      />
                      <SortableHeader
                        label="Facturado no despachado"
                        align="right"
                        active={productSort.key === "no_despachado"}
                        direction={productSort.direction}
                        onClick={() => toggleProductSort("no_despachado")}
                      />
                      <SortableHeader
                        label="Disponible"
                        align="right"
                        active={productSort.key === "disponible"}
                        direction={productSort.direction}
                        onClick={() => toggleProductSort("disponible")}
                      />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProductRows.map((row, index) => (
                      <TableRow
                        key={row.product_id}
                        style={{
                          animationDelay: `${Math.min(index * 40, 400)}ms`,
                        }}
                        className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                      >
                        <TableCell className="font-mono">
                          {row.sku ?? "—"}
                        </TableCell>
                        <TableCell className="font-medium">
                          {row.product_name}
                        </TableCell>
                        <TableCell
                          className={`text-right font-mono ${row.fisico < 0 ? "font-semibold text-red-700" : ""}`}
                        >
                          {formatNumber(row.fisico, 0)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">
                          {formatNumber(row.no_despachado, 0)}
                        </TableCell>
                        <TableCell
                          className={`text-right font-mono ${row.disponible < 0 ? "font-semibold text-red-700" : ""}`}
                        >
                          {formatNumber(row.disponible, 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="insumos" className="mt-4">
              {insumosLoading ? (
                <LoadingTable />
              ) : filteredInsumosRows.length === 0 ? (
                <EmptyState message="No hay insumos con los filtros aplicados" />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader
                        label="Código"
                        active={insumoSort.key === "code"}
                        direction={insumoSort.direction}
                        onClick={() => toggleInsumoSort("code")}
                      />
                      <SortableHeader
                        label="Nombre"
                        active={insumoSort.key === "name"}
                        direction={insumoSort.direction}
                        onClick={() => toggleInsumoSort("name")}
                      />
                      <SortableHeader
                        label="Unidad"
                        active={insumoSort.key === "unit"}
                        direction={insumoSort.direction}
                        onClick={() => toggleInsumoSort("unit")}
                      />
                      <SortableHeader
                        label="Planta"
                        active={insumoSort.key === "plant"}
                        direction={insumoSort.direction}
                        onClick={() => toggleInsumoSort("plant")}
                      />
                      <SortableHeader
                        label="Stock"
                        align="right"
                        active={insumoSort.key === "stock"}
                        direction={insumoSort.direction}
                        onClick={() => toggleInsumoSort("stock")}
                      />
                      <SortableHeader
                        label="Stock mínimo"
                        align="right"
                        active={insumoSort.key === "minimumStock"}
                        direction={insumoSort.direction}
                        onClick={() => toggleInsumoSort("minimumStock")}
                      />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInsumosRows.map((row, index) => (
                      <TableRow
                        key={`${row.kind}-${row.id}-${row.plant}`}
                        style={{
                          animationDelay: `${Math.min(index * 40, 400)}ms`,
                        }}
                        className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                      >
                        <TableCell className="font-mono">{row.code}</TableCell>
                        <TableCell className="font-medium">
                          {row.name}
                        </TableCell>
                        <TableCell>{row.unit}</TableCell>
                        <TableCell>{PLANT_LABEL[row.plant]}</TableCell>
                        <TableCell
                          className={`text-right font-mono ${row.stock < 0 ? "font-semibold text-red-700" : ""}`}
                        >
                          {formatNumber(row.stock, 0)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {row.minimumStock
                            ? formatNumber(row.minimumStock, 0)
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="materia_prima" className="mt-4">
              {materiaPrimaLoading ? (
                <LoadingTable />
              ) : filteredMateriaPrimaRows.length === 0 ? (
                <EmptyState message="No hay materias primas con los filtros aplicados" />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader
                        label="Código"
                        active={materiaPrimaSort.key === "code"}
                        direction={materiaPrimaSort.direction}
                        onClick={() => toggleMateriaPrimaSort("code")}
                      />
                      <SortableHeader
                        label="Nombre"
                        active={materiaPrimaSort.key === "name"}
                        direction={materiaPrimaSort.direction}
                        onClick={() => toggleMateriaPrimaSort("name")}
                      />
                      <SortableHeader
                        label="Unidad"
                        active={materiaPrimaSort.key === "unit"}
                        direction={materiaPrimaSort.direction}
                        onClick={() => toggleMateriaPrimaSort("unit")}
                      />
                      <SortableHeader
                        label="Planta"
                        active={materiaPrimaSort.key === "plant"}
                        direction={materiaPrimaSort.direction}
                        onClick={() => toggleMateriaPrimaSort("plant")}
                      />
                      <SortableHeader
                        label="Stock"
                        align="right"
                        active={materiaPrimaSort.key === "stock"}
                        direction={materiaPrimaSort.direction}
                        onClick={() => toggleMateriaPrimaSort("stock")}
                      />
                      <SortableHeader
                        label="Stock mínimo"
                        align="right"
                        active={materiaPrimaSort.key === "minimumStock"}
                        direction={materiaPrimaSort.direction}
                        onClick={() => toggleMateriaPrimaSort("minimumStock")}
                      />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMateriaPrimaRows.map((row, index) => (
                      <TableRow
                        key={`${row.kind}-${row.id}-${row.plant}`}
                        style={{
                          animationDelay: `${Math.min(index * 40, 400)}ms`,
                        }}
                        className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                      >
                        <TableCell className="font-mono">{row.code}</TableCell>
                        <TableCell className="font-medium">
                          {row.name}
                        </TableCell>
                        <TableCell>{row.unit}</TableCell>
                        <TableCell>{PLANT_LABEL[row.plant]}</TableCell>
                        <TableCell
                          className={`text-right font-mono ${row.stock < 0 ? "font-semibold text-red-700" : ""}`}
                        >
                          {formatNumber(row.stock, 2)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {row.minimumStock
                            ? formatNumber(row.minimumStock, 2)
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </PageContainer>
  );
}

function LoadingTable() {
  return (
    <div className="space-y-2">
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

function SortableHeader({
  label,
  active,
  direction,
  onClick,
  align = "left",
}: {
  label: string;
  active: boolean;
  direction: SortDirection;
  onClick: () => void;
  align?: "left" | "right";
}) {
  return (
    <TableHead className={align === "right" ? "text-right" : undefined}>
      <button
        type="button"
        onClick={onClick}
        className={`flex items-center gap-1 hover:text-foreground ${
          align === "right" ? "ml-auto" : ""
        }`}
      >
        {label}
        {active ? (
          direction === "asc" ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
        )}
      </button>
    </TableHead>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="py-12 text-center">
      <PackageSearch className="mx-auto h-12 w-12 text-muted-foreground/40" />
      <p className="mt-4 text-muted-foreground">{message}</p>
    </div>
  );
}
